import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Reveal from './Reveal'

const faqs = [
  {
    q: '¿Cómo accedo al curso después de comprarlo?',
    a: 'Al completar la compra en Hotmart recibirás un email con tus credenciales. Podés ingresar desde cualquier dispositivo, en cualquier momento. Dependiendo de la suscripción elegida, el acceso será mensual o anual.',
  },
  {
    q: '¿Qué métodos de pago aceptan?',
    a: 'A través de Hotmart podés pagar con tarjeta de crédito o débito, transferencia, PayPal y medios locales disponibles en tu país. En muchos países hay opciones en cuotas.',
  },
  {
    q: '¿Hay garantía de devolución?',
    a: 'Sí. Todos los cursos incluyen garantía de 7 días. Si no estás satisfecho por cualquier motivo, te devolvemos el 100% del importe sin preguntas.',
  },
  {
    q: '¿El contenido se actualiza?',
    a: 'Sí. Las actualizaciones están incluidas sin costo adicional. Comprando el curso tenés acceso a todas las versiones futuras.',
  },
  {
    q: '¿Cómo funcionan las mentorías 1 a 1?',
    a: 'Completás el formulario, coordinamos una llamada inicial gratuita de 20 minutos para evaluar el fit, y armamos el plan personalizado. Las sesiones son por videollamada con seguimiento por WhatsApp.',
  },
  {
    q: '¿Se puede acceder desde el celular?',
    a: 'Sí, la plataforma Hotmart está optimizada para mobile. Podés estudiar desde tu teléfono, tablet o computadora sin diferencia de experiencia.',
  },
]

function FaqItem({ faq, index }) {
  const [open, setOpen] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      style={{ borderBottom: '1px solid var(--line)' }}
    >
      <button
        className="w-full flex items-center justify-between gap-6 py-7 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <span
          className="font-display font-semibold text-[15px] leading-[1.4]"
          style={{ color: open ? 'var(--gold)' : 'var(--ink)', transition: 'color 0.2s' }}
        >
          {faq.q}
        </span>
        <motion.div
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="w-7 h-7 shrink-0 flex items-center justify-center font-display font-light text-[18px]"
          style={{
            border: '1px solid var(--line)',
            color: 'var(--gold)',
            background: open ? 'rgba(201,168,76,0.1)' : 'transparent',
            transition: 'background 0.2s',
          }}
        >
          +
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p
              className="font-body font-light text-[14px] leading-[1.9] pb-7"
              style={{ color: 'var(--ink-2)' }}
            >
              {faq.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function SmallHelixDecor() {
  return (
    <svg
      width="160" height="500" viewBox="0 0 160 500" fill="none"
      className="absolute right-[5vw] top-0 h-full opacity-[0.04] pointer-events-none"
      preserveAspectRatio="xMidYMid slice"
    >
      {Array.from({ length: 20 }).map((_, i) => {
        const y = i * 26
        const phase = (i / 20) * Math.PI * 6
        const x0 = 80 + Math.sin(phase) * 50
        const x1 = 80 + Math.sin(phase + Math.PI) * 50
        return (
          <g key={i}>
            <line x1={x0} y1={y} x2={x1} y2={y} stroke="#C9A84C" strokeWidth="0.75"/>
            <circle cx={x0} cy={y} r="3.5" fill="#C9A84C"/>
            <circle cx={x1} cy={y} r="3.5" stroke="#C9A84C" strokeWidth="0.75"/>
          </g>
        )
      })}
    </svg>
  )
}

export default function FAQ() {
  return (
    <section
      id="faq"
      className="relative overflow-hidden"
      style={{ padding: '120px 5vw', background: 'var(--bg-2)' }}
    >
      <SmallHelixDecor />

      <div className="relative z-10 max-w-[760px] mx-auto">
        <Reveal>
          <div className="section-label justify-center">Preguntas frecuentes</div>
          <h2
            className="font-display font-black leading-[1.04] tracking-[-0.02em] mb-16 text-center"
            style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', color: 'var(--ink)' }}
          >
            Todo lo que necesitás{' '}
            <em className="not-italic" style={{ color: 'var(--gold)' }}>saber</em>.
          </h2>
        </Reveal>

        <div style={{ borderTop: '1px solid var(--line)' }}>
          {faqs.map((faq, i) => (
            <FaqItem key={faq.q} faq={faq} index={i} />
          ))}
        </div>

        {/* WhatsApp CTA */}
        <Reveal delay={0.2}>
          <div
            className="text-center mt-16 pt-16"
            style={{ borderTop: '1px solid var(--line)' }}
          >
            <p
              className="font-body font-light text-[15px] mb-6"
              style={{ color: 'var(--ink-2)' }}
            >
              ¿Tenés una pregunta que no encontraste aquí?
            </p>
            <motion.a
              href="#"
              className="btn-ghost inline-flex items-center gap-2.5"
              whileHover={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: '#25D366' }} />
              Consultar por WhatsApp
            </motion.a>
          </div>
        </Reveal>
      </div>
    </section>
  )
}